/**
 * 放送地点単位で選択可能な Amazon Polly の VoiceId 選択肢定義。
 *
 * - システムは Polly を `Engine=neural` 固定で使用するため、日本語 Neural 対応の
 *   VoiceId のみを列挙する。Standard 専用 (例: `Mizuki`) は Neural では合成できず
 *   `ValidationException` になるため意図的に除外している。
 * - `BroadcastPointForm` の音声セレクトと、`BroadcastContentsEditor` の音声プレビュー
 *   (POST /audio-preview の `voice_id`) の両方で共有する。
 * - バックエンド (`lambda/control-panel-api/index.py` の `ALLOWED_VOICE_IDS`) と
 *   OpenAPI schema (`docs/openapi/control-panel-api.yaml` の `voice_id` enum) と
 *   キーが一致していること。
 */

/** 1 件ぶんの Polly VoiceId 選択肢 */
export interface PollyVoiceOption {
  /** Polly の VoiceId (例: `"Takumi"`)。API / DB にはこの値がそのまま保存される。 */
  readonly id: string;
  /** UI 表示用ラベル (例: `"Takumi（男性・日本語 Neural）"`)。 */
  readonly label: string;
}

/**
 * 選択可能な Polly VoiceId (日本語 Neural 対応) の一覧。
 *
 * NOTE: 追加/削除する際は以下 4 箇所を必ず同期させること。
 *   1. 本ファイル (`POLLY_VOICE_OPTIONS`)
 *   2. `docs/openapi/control-panel-api.yaml` の `voice_id` enum
 *   3. `lambda/control-panel-api/index.py` の `ALLOWED_VOICE_IDS`
 *   4. AWS Polly が当該 VoiceId で `Engine=neural` の合成をサポートしていること
 */
export const POLLY_VOICE_OPTIONS: readonly PollyVoiceOption[] = [
  { id: 'Takumi', label: 'Takumi（男性・日本語 Neural）' },
  { id: 'Kazuha', label: 'Kazuha（女性・日本語 Neural）' },
  { id: 'Tomoko', label: 'Tomoko（女性・日本語 Neural）' },
];

/**
 * `voice_id` 未指定時に使用するシステムデフォルト。
 * 現行の実放送・プレビューが従来から `Takumi` 固定だったため、後方互換のため同値。
 */
export const DEFAULT_POLLY_VOICE_ID = 'Takumi';

/** `POLLY_VOICE_OPTIONS` の id のみを取り出した配列 (バリデーション用) */
export const POLLY_VOICE_IDS: readonly string[] = POLLY_VOICE_OPTIONS.map((o) => o.id);

/**
 * 与えられた voice_id が有効か (許容リストに含まれるか) を判定する。
 * 空文字 / undefined / 未登録の値はすべて false を返す。
 */
export function isValidPollyVoiceId(value: string | undefined | null): value is string {
  if (typeof value !== 'string' || value === '') return false;
  return POLLY_VOICE_IDS.includes(value);
}
